import { useEffect, useState } from 'react';
import { agentsApi } from '../api/apiAgents';
import { showSnackbar } from './SnackbarAlert'; // Assuming SnackbarAlert is in the same directory
import { UserIcon, EnvelopeIcon, PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/solid';

function Agents() {
    const [agents, setAgents] = useState([]);
    const [newAgentName, setNewAgentName] = useState('');
    const [newAgentEmail, setNewAgentEmail] = useState('');
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [loadingConfirm, setLoadingConfirm] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showResultDialog, setShowResultDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null); // 'delete' or 'update'
    const [agentToActOn, setAgentToActOn] = useState(null); // Stores the agent object for delete/update
    const [showEditModal, setShowEditModal] = useState(false);
    const [editAgentName, setEditAgentName] = useState('');
    const [editAgentEmail, setEditAgentEmail] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            // As per user instruction, send an empty list in the 'informacion' node for consultation
            const response = await agentsApi.post('/agentes_mercately', { informacion: [''] });
            if (response && response.status === 'success' && Array.isArray(response.informacion)) {
                setAgents(response.informacion);
            } else {
                console.error("Failed to fetch agents:", response);
                setAgents([]);
                showSnackbar(response?.message || 'Error fetching agents.', 'error');
            }
        } catch (error) {
            console.error("Error fetching agents:", error);
            showSnackbar('Error fetching agents.', 'error');
        }
    };

    const handleAddAgent = async () => {
        if (newAgentName.trim() === '' || newAgentEmail.trim() === '') {
            setDialogMessage('Ingresa el nombre del agente y su correo electrónico.');
            setShowResultDialog(true);
            return;
        }
        setLoadingAdd(true);
        try {
            const response = await agentsApi.post('/agentes_mercately/crear', {
                nombre: newAgentName,
                correo: newAgentEmail,
            });
            if (response && response.status === 'success') {
                showSnackbar('Agent added successfully!', 'success');
                setNewAgentName('');
                setNewAgentEmail('');
                fetchAgents(); // Refresh the list
            } else {
                showSnackbar(response?.message || 'Error adding agent.', 'error');
            }
        } catch (error) {
            console.error('Error adding agent:', error);
            showSnackbar('Error adding agent.', 'error');
        } finally {
            setLoadingAdd(false);
        }
    };

    const handleUpdateAgent = async () => {
        if (!agentToActOn || editAgentName.trim() === '' || editAgentEmail.trim() === '') {
            setDialogMessage('Invalid agent data for update.');
            setShowResultDialog(true);
            return;
        }
        setLoadingConfirm(true);
        try {
            const response = await agentsApi.post('/agentes_mercately/actualizar', {
                nombre: editAgentName, // The name is used as identifier for update
                correo: editAgentEmail, // The new email
            });
            if (response && response.status === 'success') {
                showSnackbar('Agent updated successfully!', 'success');
                fetchAgents(); // Refresh the list
                setShowEditModal(false);
            } else {
                showSnackbar(response?.message || 'Error updating agent.', 'error');
            }
        } catch (error) {
            console.error('Error updating agent:', error);
            showSnackbar('Error updating agent.', 'error');
        } finally {
            setLoadingConfirm(false);
            setShowConfirmDialog(false); // Close confirmation dialog if it was open
        }
    };

    const handleDeleteAgent = async () => {
        if (!agentToActOn) {
            setDialogMessage('No agent selected for deletion.');
            setShowResultDialog(true);
            return;
        }
        setLoadingConfirm(true);
        try {
            const response = await agentsApi.post('/agentes_mercately/borrar', {
                nombre: agentToActOn.nombre,
                correo: agentToActOn.correo,
            });
            if (response && response.status === 'success') {
                showSnackbar('Agent deleted successfully!', 'success');
                fetchAgents(); // Refresh the list
            } else {
                showSnackbar(response?.message || 'Error deleting agent.', 'error');
            }
        } catch (error) {
            console.error('Error deleting agent:', error);
            showSnackbar('Error deleting agent.', 'error');
        } finally {
            setLoadingConfirm(false);
            setShowConfirmDialog(false);
        }
    };

    const openConfirmDialog = (action, agent) => {
        setAgentToActOn(agent);
        setConfirmAction(action);
        setDialogMessage(`Are you sure you want to ${action} agent "${agent.nombre}"?`);
        setShowConfirmDialog(true);
    };

    const openEditModal = (agent) => {
        setAgentToActOn(agent);
        setEditAgentName(agent.nombre);
        setEditAgentEmail(agent.correo);
        setShowEditModal(true);
    };

    const handleConfirmAction = () => {
        if (confirmAction === 'delete') {
            handleDeleteAgent();
        } else if (confirmAction === 'update') {
            handleUpdateAgent();
        }
    };

    const filteredAgents = agents.filter(agent =>
        agent.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.correo.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 pt-0 mt-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <h1 className="text-2xl font-bold mb-4">Mercately Agents Management</h1>

            {/* Add New Agent Section */}
            <div className="mb-6 p-4 border rounded-lg shadow-md bg-white">
                <h2 className="text-xl font-semibold mb-3">Add New Agent</h2>
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <input
                        type="text"
                        value={newAgentName}
                        onChange={(e) => setNewAgentName(e.target.value)}
                        placeholder="Agent Name"
                        className="flex-1 p-2 border rounded-lg text-lg"
                    />
                    <input
                        type="email"
                        value={newAgentEmail}
                        onChange={(e) => setNewAgentEmail(e.target.value)}
                        placeholder="Agent Email"
                        className="flex-1 p-2 border rounded-lg text-lg"
                    />
                    <button
                        onClick={handleAddAgent}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                        disabled={loadingAdd}
                    >
                        <PlusIcon className="h-5 w-5 mr-2" /> Add Agent
                        {loadingAdd && (
                            <svg className="animate-spin ml-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Search and Agents List */}
            <div className="mb-6">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search agents by name or email..."
                    className="w-full p-2 border rounded-lg text-lg"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAgents.map((agent, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow-md flex flex-col space-y-2">
                        <div className="flex items-center space-x-2">
                            <UserIcon className="h-5 w-5 text-gray-600" />
                            <span className="font-semibold text-gray-800">{agent.nombre}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <EnvelopeIcon className="h-5 w-5 text-gray-600" />
                            <span className="text-gray-700">{agent.correo}</span>
                        </div>
                        <div className="flex justify-end space-x-2 mt-3">
                            <button
                                onClick={() => openEditModal(agent)}
                                className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                            >
                                <PencilIcon className="h-4 w-4 mr-1" /> Edit
                            </button>
                            <button
                                onClick={() => openConfirmDialog('delete', agent)}
                                className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
                            >
                                <TrashIcon className="h-4 w-4 mr-1" /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-[60]">
                    <div className="bg-white p-6 rounded-md shadow-lg">
                        <p className="mb-4">{dialogMessage}</p>
                        <div className="mt-4 flex justify-end">
                            <button
                                className="mr-2 px-4 py-2 bg-gray-300 rounded-md"
                                onClick={() => setShowConfirmDialog(false)}
                                disabled={loadingConfirm}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-4 py-2 text-white rounded-md transition-colors ${
                                    confirmAction === 'delete' ? 'bg-red-500 hover:bg-red-700' : 'bg-blue-500 hover:bg-blue-700'
                                } flex items-center`}
                                onClick={handleConfirmAction}
                                disabled={loadingConfirm}
                            >
                                {confirmAction === 'delete' ? 'Delete' : 'Save'}
                                {loadingConfirm && (
                                    <svg className="animate-spin ml-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Agent Modal */}
            {showEditModal && agentToActOn && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-md shadow-lg w-96">
                        <h2 className="text-xl font-semibold mb-4">Edit Agent</h2>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">Name:</label>
                            <input
                                type="text"
                                value={editAgentName}
                                onChange={(e) => setEditAgentName(e.target.value)}
                                className="w-full p-2 border rounded-lg"
                                disabled // Name is the identifier, usually not editable
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">Email:</label>
                            <input
                                type="email"
                                value={editAgentEmail}
                                onChange={(e) => setEditAgentEmail(e.target.value)}
                                className="w-full p-2 border rounded-lg"
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button
                                className="px-4 py-2 bg-gray-300 rounded-md"
                                onClick={() => setShowEditModal(false)}
                                disabled={loadingConfirm}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                                onClick={() => openConfirmDialog('update', { nombre: editAgentName, correo: editAgentEmail })}
                                disabled={loadingConfirm}
                            >
                                <PencilIcon className="h-5 w-5 mr-2" /> Save Changes
                                {loadingConfirm && (
                                    <svg className="animate-spin ml-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Result Dialog (for general messages) */}
            {showResultDialog && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-md shadow-lg">
                        <p>{dialogMessage}</p>
                        <div className="mt-4 flex justify-end">
                            <button
                                className="px-4 py-2 bg-blue-500 text-white rounded-md"
                                onClick={() => setShowResultDialog(false)}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Agents;